"use client"

import Link from "next/link"
import { ArrowRight, CalendarDays, Download, Mail, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { CtaAudience, CtaForm, CtaShellProps } from "./cta-types"

export function CtaFormIsland({ form, compact = false }: { form: CtaForm; compact?: boolean }) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")
  const [errors, setErrors] = useState<Record<string, string>>({})

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const nextErrors: Record<string, string> = {}
    for (const field of form.fields) {
      const value = String(data.get(field.name) ?? "").trim()
      if (field.required && !value) nextErrors[field.name] = `${field.label} is required.`
      if (field.type === "email" && value && !/^\S+@\S+\.\S+$/.test(value)) nextErrors[field.name] = "Enter a valid email address."
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    setStatus("loading")
    window.setTimeout(() => setStatus("success"), 650)
  }

  return (
    <form action={form.action} onSubmit={handleSubmit} noValidate className={cn("grid gap-3 rounded-panel border border-border/70 bg-background/90 p-3 text-foreground shadow-elegant backdrop-blur-xl", compact ? "sm:grid-cols-[1fr_auto]" : "sm:grid-cols-2")}> 
      {form.fields.map((field) => {
        const errorId = `${field.name}-error`
        const textarea = field.type === "textarea"
        return (
          <label key={field.name} className={cn("grid gap-2 text-sm font-semibold", textarea && "sm:col-span-2")}>
            <span>{field.label}</span>
            {textarea ? (
              <textarea name={field.name} placeholder={field.placeholder} required={field.required} aria-describedby={errors[field.name] ? errorId : undefined} className="min-h-28 rounded-card border border-input bg-background px-4 py-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            ) : (
              <Input type={field.type === "company" ? "text" : field.type ?? "text"} name={field.name} placeholder={field.placeholder} required={field.required} aria-describedby={errors[field.name] ? errorId : undefined} className="h-12 rounded-full bg-background" />
            )}
            {errors[field.name] && <span id={errorId} className="text-xs font-medium text-destructive">{errors[field.name]}</span>}
          </label>
        )
      })}
      <Button type="submit" size="lg" className={cn("h-12 rounded-full", compact ? "self-end" : "sm:col-span-2")} disabled={status === "loading"}>
        {status === "loading" ? "Sending" : form.submitLabel ?? "Submit"}
        <ArrowRight className="size-4" />
      </Button>
      <p className="min-h-5 text-sm text-muted-foreground sm:col-span-2" aria-live="polite">
        {status === "success" ? form.successMessage ?? "Thanks. We will follow up soon." : status === "error" ? "Something went wrong. Please try again." : ""}
      </p>
    </form>
  )
}

export function CtaCountdownIsland({ target, label = "Offer ends", completeLabel = "Offer ended" }: { target: string; label?: string; completeLabel?: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [])
  const remaining = Math.max(0, new Date(target).getTime() - now)
  const complete = remaining === 0
  const days = Math.floor(remaining / 86400000)
  const hours = Math.floor((remaining / 3600000) % 24)
  const minutes = Math.floor((remaining / 60000) % 60)
  const seconds = Math.floor((remaining / 1000) % 60)
  return (
    <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-2 text-xs font-semibold text-foreground shadow-sm" aria-live="polite">
      <span className="text-muted-foreground">{complete ? completeLabel : label}</span>
      {!complete && [days, hours, minutes, seconds].map((value, index) => <span key={index} className="rounded-full bg-muted px-2 py-1 tabular-nums">{String(value).padStart(2, "0")}</span>)}
    </div>
  )
}

export function DismissibleCtaIsland({ storageKey, children }: { storageKey: string; children: React.ReactNode }) {
  const [hidden, setHidden] = useState(false)
  useEffect(() => setHidden(window.localStorage.getItem(storageKey) === "dismissed"), [storageKey])
  if (hidden) return null
  return (
    <div className="relative">
      {children}
      <button type="button" aria-label="Dismiss" className="absolute right-3 top-3 z-10 grid size-9 place-items-center rounded-full bg-background/82 text-foreground shadow-sm backdrop-blur-xl" onClick={() => { window.localStorage.setItem(storageKey, "dismissed"); setHidden(true) }}>
        <X className="size-4" />
      </button>
    </div>
  )
}

export function StickyCtaIsland({ children, storageKey = "hiveku-sticky-cta" }: { children: React.ReactNode; storageKey?: string }) {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  useEffect(() => {
    setDismissed(window.localStorage.getItem(storageKey) === "dismissed")
    const update = () => setVisible(window.scrollY > 520)
    update()
    window.addEventListener("scroll", update, { passive: true })
    return () => window.removeEventListener("scroll", update)
  }, [storageKey])
  if (!visible || dismissed) return null
  return (
    <div className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-4xl rounded-panel border border-border/70 bg-background/94 p-3 shadow-elegant backdrop-blur-xl md:bottom-5">
      {children}
      <button type="button" aria-label="Dismiss" className="absolute -right-2 -top-2 grid size-8 place-items-center rounded-full bg-foreground text-background" onClick={() => { window.localStorage.setItem(storageKey, "dismissed"); setDismissed(true) }}><X className="size-4" /></button>
    </div>
  )
}

export function ExitIntentCtaIsland({ children, storageKey = "hiveku-exit-cta" }: { children: React.ReactNode; storageKey?: string }) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (window.localStorage.getItem(storageKey) === "dismissed") return
    let previousY = window.scrollY
    const mouse = (event: MouseEvent) => { if (event.clientY < 20) setOpen(true) }
    const scroll = () => { if (previousY - window.scrollY > 80) setOpen(true); previousY = window.scrollY }
    window.addEventListener("mouseout", mouse)
    window.addEventListener("scroll", scroll, { passive: true })
    return () => { window.removeEventListener("mouseout", mouse); window.removeEventListener("scroll", scroll) }
  }, [storageKey])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-foreground/70 p-4 backdrop-blur-xl" role="dialog" aria-modal="true" aria-label="Exit offer">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-panel bg-background shadow-elegant">
        <button type="button" aria-label="Dismiss" className="absolute right-4 top-4 z-10 grid size-10 place-items-center rounded-full bg-muted text-foreground" onClick={() => { window.localStorage.setItem(storageKey, "dismissed"); setOpen(false) }}><X className="size-5" /></button>
        {children}
      </div>
    </div>
  )
}

export function AudienceTabsCtaIsland({ audiences }: { audiences: CtaAudience[] }) {
  const [active, setActive] = useState(0)
  const item = audiences[active]
  if (!item) return null
  return (
    <div className="rounded-panel border border-border/70 bg-card p-3 text-foreground shadow-elegant">
      <div className="grid gap-2 rounded-full bg-muted p-1 sm:grid-cols-2">
        {audiences.slice(0, 3).map((audience, index) => <button key={audience.label} type="button" className={cn("rounded-full px-4 py-2 text-sm font-semibold", active === index ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")} onClick={() => setActive(index)}>{audience.label}</button>)}
      </div>
      <div className="p-5">
        <h3 className="font-heading text-3xl font-semibold tracking-tight">{item.headline}</h3>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">{item.supportingLine}</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button asChild className="rounded-full"><Link href={item.primaryCta.href}>{item.primaryCta.label}<ArrowRight /></Link></Button>
          {item.secondaryCta && <Button asChild variant="outline" className="rounded-full"><Link href={item.secondaryCta.href}>{item.secondaryCta.label}</Link></Button>}
        </div>
      </div>
    </div>
  )
}

export function StoreBadgesIsland({ appStoreHref = "#app-store", playStoreHref = "#play-store" }: { appStoreHref?: string; playStoreHref?: string }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      <Button asChild size="lg" className="rounded-card bg-foreground text-background hover:bg-foreground/90"><Link href={appStoreHref}><Download className="size-4" /> App Store</Link></Button>
      <Button asChild size="lg" variant="outline" className="rounded-card bg-background text-foreground hover:bg-muted"><Link href={playStoreHref}><Download className="size-4" /> Google Play</Link></Button>
    </div>
  )
}

export function SchedulerButtonIsland({ href = "#calendar", label = "Book a time" }: { href?: string; label?: string }) {
  return <Button asChild size="lg" className="rounded-full"><Link href={href}><CalendarDays className="size-4" />{label}</Link></Button>
}

export function NewsletterCompactIsland({ form }: { form: CtaForm }) {
  const compactForm = useMemo(() => ({ ...form, fields: form.fields.length ? form.fields : [{ name: "email", label: "Email", type: "email" as const, required: true }] }), [form])
  return <CtaFormIsland form={compactForm} compact />
}

export function MailDownloadHint() {
  return <span className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground"><Mail className="size-4" /> Delivered instantly by email</span>
}

export function StickyCtaContent({ headline, primaryCta, trustMicrocopy }: Pick<CtaShellProps, "headline" | "primaryCta" | "trustMicrocopy">) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div><p className="font-heading text-lg font-semibold tracking-tight">{headline}</p>{trustMicrocopy && <p className="text-sm text-muted-foreground">{trustMicrocopy}</p>}</div>
      {primaryCta && <Button asChild className="rounded-full"><Link href={primaryCta.href}>{primaryCta.label}<ArrowRight /></Link></Button>}
    </div>
  )
}
